import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './ContactComponent.scss';
import ButtonComponent from '../ButtonComponent/ButtonComponent';
import { FaWhatsapp, FaEnvelope, FaInstagram, FaFacebook } from 'react-icons/fa';
import emailjs from '@emailjs/browser'

// Firestore
//import { db } from '../firebase/firebase'; // ContactComponent/ -> ../firebase.js
import { db, addDoc, collection, serverTimestamp } from '../firebase/firebase';


const ContactComponent = () => {
  const sectionRef = useRef(null);
  const leftRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  // Reveal-on-scroll once
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Only used for desktop equal-height (optional). Kept simple now.
  useLayoutEffect(() => {
    const el = sectionRef.current;
    const left = leftRef.current;
    if (!el || !left) return;
    const update = () => {
      // No forced equal height to avoid ResizeObserver loops
      el.style.removeProperty('--left-panel-h');
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(left);
    return () => ro.disconnect();
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (errors.general) setErrors((prev) => ({ ...prev, general: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Required';
    if (!form.email.trim()) next.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email';
    if (!form.message.trim()) next.message = 'Required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSubmitting(true);
      setSent(false);

      // Write to Firestore
      await addDoc(collection(db, 'contactusresponses'), {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        createdAt: serverTimestamp(),
        source: 'website-contact-form',
      });

      setSent(true);
      setForm({ name: '', email: '', message: '' });
      setErrors({});
    } catch (err) {
      console.error(err);
      setErrors((prev) => ({
        ...prev,
        general: 'Something went wrong while sending your message. Please try again.',
      }));
    } finally {
      setSubmitting(false);
    }

    try {
      const REACT_APP_TEMPLATE_ID = process.env.REACT_APP_TEMPLATE_ID_CONTACT_US;
        //Send email
        emailjs
          .send(
            process.env.REACT_APP_SERVICE,
            REACT_APP_TEMPLATE_ID,
            {
              name: form.name.trim(),
              email: form.email.trim(),
              message: form.message.trim(),
            },
            process.env.REACT_APP_PUBLIC_ID
          )
          .then(() => {
            console.log("Email sent successfully");
          })
          .catch((error) => {
            console.error("EmailJS error:", error?.text || error.message || JSON.stringify(error));
          });

      } catch (error) {
        console.error("Error updating guest status:", error);
      }
  };

  return (
    <section
      ref={sectionRef}
      className={`contact ${visible ? 'is-visible' : ''}`}
      id="contact"
      aria-label="Contact Us"
    >
      <header className="contact__head">
        <p className="contact__eyebrow">We’re here for you</p>
        <h2 className="contact__title">Contact Us</h2>
        <p className="contact__sub">Need help? Just send us a WhatsApp message. We’d love to hear from you!</p>
        <p className="contact__friendly">
          We’re super friendly and flexible. Our main objective is the satisfaction of our
          customers. We truly care about your planning journey.
        </p>
      </header>

      <div className="contact__grid">
        {/* Left column: contact channels */}
        <aside ref={leftRef} className="contact__panel" aria-label="Contact channels">
          <div className="contact__cards">
            <a className="ccard" href="https://wa.me/94705367980" target="_blank" rel="noreferrer">
              <span className="ccard__icon"><FaWhatsapp /></span>
              <div className="ccard__meta">
                <span className="ccard__label">WhatsApp</span>
                <span className="ccard__value">+94 70 536 7980</span>
              </div>
            </a>

            <a className="ccard" href="mailto:hello@planmyguest.com">
              <span className="ccard__icon"><FaEnvelope /></span>
              <div className="ccard__meta">
                <span className="ccard__label">Email</span>
                <span className="ccard__value">planmyguestbookings@gmail.com</span>
              </div>
            </a>

            <a className="ccard" href="#" aria-label="Instagram">
              <span className="ccard__icon"><FaInstagram /></span>
              <div className="ccard__meta">
                <span className="ccard__label">Instagram</span>
                <span className="ccard__value">@planmyguest</span>
              </div>
            </a>

            <a className="ccard" href="#" aria-label="Facebook">
              <span className="ccard__icon"><FaFacebook /></span>
              <div className="ccard__meta">
                <span className="ccard__label">Facebook</span>
                <span className="ccard__value">Plan My Guest</span>
              </div>
            </a>
          </div>
        </aside>

        {/* Right column: form */}
        <form className="cform" onSubmit={onSubmit} aria-label="Contact form" noValidate>
          {errors.general && <div className="cform__banner cform__banner--error">{errors.general}</div>}
          {sent && !errors.general && (
            <div className="cform__banner cform__banner--ok">
              Thanks! We received your message and will get back to you soon.
            </div>
          )}

          <div className="cform__row">
            <label className="cform__label">
              Your name
              <input
                type="text"
                name="name"
                className={`cform__input ${errors.name ? 'has-error' : ''}`}
                value={form.name}
                onChange={onChange}
                placeholder="Your full name"
                disabled={submitting}
              />
              {errors.name && <span className="cform__error">{errors.name}</span>}
            </label>

            <label className="cform__label">
              Email
              <input
                type="email"
                name="email"
                className={`cform__input ${errors.email ? 'has-error' : ''}`}
                value={form.email}
                onChange={onChange}
                placeholder="you@example.com"
                disabled={submitting}
              />
              {errors.email && <span className="cform__error">{errors.email}</span>}
            </label>
          </div>
          <br/>
          <label className="cform__label cform__label--message">
            Message
            <textarea
              name="message"
              className={`cform__input cform__textarea ${errors.message ? 'has-error' : ''}`}
              value={form.message}
              onChange={onChange}
              placeholder="Tell us about your wedding plans or any questions you have…"
              rows={7}
              disabled={submitting}
            />
            {errors.message && <span className="cform__error">{errors.message}</span>}
          </label>

          <div className="cform__actions">
            <ButtonComponent type="submit" variant="primary" className="cform__submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send Message'}
            </ButtonComponent>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactComponent;
